import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { InvitesTab } from "@/components/account/InvitesTab";
import { TokensTab } from "@/components/account/TokensTab";
import { OutputSelector } from "@/components/playbar/OutputSelector";
import { api } from "@/lib/api";
import { sfx } from "@/lib/sfx";
import { useAuth } from "@/hooks/useAuth";
import { usePlayer } from "@/hooks/usePlayer";
import type { Me } from "@/types/types";

interface P { open: boolean; onClose: () => void }

const input = {
  padding: "10px 12px", background: "var(--bg-elev-2)",
  border: "1px solid var(--line)", borderRadius: 8, color: "var(--fg)", width: "100%",
  fontSize: 14,
} as const;

export function SettingsModal({ open, onClose }: P) {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState<"account" | "security" | "email" | "sound" | "invites" | "tokens">("account");
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Reset scroll to top whenever the tab changes so the user always lands on
  // the top of the freshly-mounted panel (was leaving them scrolled to the
  // bottom, seeing only the SFX/crossfade footer).
  useLayoutEffect(() => {
    bodyRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [tab]);

  return (
    <Modal open={open} onClose={onClose} title="Paramètres" width={640}>
      {/* Sticky tab bar — always visible even when the panel below scrolls */}
      <div style={{
        display: "flex", gap: 4, borderBottom: "1px solid var(--line)", marginBottom: 16,
        position: "sticky", top: 0, zIndex: 2,
        background: "var(--bg-elev)",
        overflowX: "auto", scrollbarWidth: "none",
      }}>
        {[
          ["account", "Compte"],
          ["security", "Mot de passe"],
          ["email", "Email"],
          ["sound", "Son"],
          ["invites", "Invités"],
          ["tokens", "API"],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key as typeof tab)}
                  style={{
                    padding: "10px 14px", background: "transparent",
                    color: tab === key ? "var(--fg)" : "var(--fg-mute)",
                    borderBottom: tab === key ? "2px solid var(--accent)" : "2px solid transparent",
                    borderRadius: 0, cursor: "pointer", fontWeight: 600,
                    flexShrink: 0,
                  }}>
            {label}
          </button>
        ))}
      </div>

      {/* Tab body — scrolls independently, resets on tab switch */}
      <div ref={bodyRef} style={{ maxHeight: "58vh", overflowY: "auto", paddingRight: 4 }}>
        {tab === "account" && <UsernameTab user={user} />}
        {tab === "security" && <PasswordTab />}
        {tab === "email" && <EmailTab user={user} />}
        {tab === "sound" && <SoundTab />}
        {tab === "invites" && <InvitesTab />}
        {tab === "tokens" && <TokensTab />}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
        <button className="btn" onClick={() => void logout()}>Se déconnecter</button>
        <button className="btn primary" onClick={onClose}>Fermer</button>
      </div>
    </Modal>
  );
}

function SoundTab() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <div style={{
        padding: "10px 12px", background: "var(--bg-elev-2)", borderRadius: 8,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Sortie audio</div>
        <OutputSelector />
      </div>
      <SfxToggle />
      <CrossfadeSlider />
    </div>
  );
}

function CrossfadeSlider() {
  const { crossfadeMs, setCrossfadeMs } = usePlayer();
  return (
    <label style={{
      display: "block", marginTop: 10,
      padding: "10px 12px", background: "var(--bg-elev-2)", borderRadius: 8,
      cursor: "pointer", userSelect: "none",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontWeight: 600 }}>Fondu enchaîné (auto-DJ)</div>
        <div style={{ fontVariantNumeric: "tabular-nums", color: "var(--fg-mute)" }}>
          {crossfadeMs === 0 ? "off" : `${(crossfadeMs / 1000).toFixed(1)} s`}
        </div>
      </div>
      <input type="range" min={0} max={10000} step={500}
             value={crossfadeMs} onChange={(e) => setCrossfadeMs(Number(e.target.value))}
             style={{ width: "100%", marginTop: 6, accentColor: "var(--accent)" }} />
      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        Transition douce entre deux morceaux au lieu d'un cut sec. 0 = désactivé.
      </div>
    </label>
  );
}

function SfxToggle() {
  const [on, setOn] = useState(() => sfx.isEnabled());
  return (
    <label style={{
      display: "flex", alignItems: "center", gap: 10, marginTop: 18,
      padding: "10px 12px", background: "var(--bg-elev-2)", borderRadius: 8,
      cursor: "pointer", userSelect: "none",
    }}>
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => {
          setOn(e.target.checked);
          sfx.setEnabled(e.target.checked);
          if (e.target.checked) sfx.chirp();
        }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>Sons d'interface</div>
        <div className="muted" style={{ fontSize: 12 }}>
          Petits clics, pops et blips synthétisés à chaque interaction. Peut être désactivé.
        </div>
      </div>
    </label>
  );
}

function useSetter() {
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const wrap = useCallback(async (fn: () => Promise<string>) => {
    setBusy(true); setMsg(null);
    try { setMsg({ ok: true, text: await fn() }); }
    catch (e) { setMsg({ ok: false, text: e instanceof Error ? e.message : "Erreur" }); }
    finally { setBusy(false); }
  }, []);
  return { msg, busy, wrap };
}

function UsernameTab({ user }: { user: Me | null }) {
  const [newU, setNewU] = useState(user?.username ?? "");
  const [pw, setPw] = useState("");
  const [check, setCheck] = useState<{ available?: boolean; reason?: string }>({});
  const { msg, busy, wrap } = useSetter();
  const { user: _u, ...rest } = useAuth();
  void _u; void rest;

  useEffect(() => {
    const same = newU === user?.username || newU.length < 3;
    if (same) { setCheck({}); return; }
    const id = setTimeout(async () => {
      try {
        const r = await api<{ available: boolean; reason?: string }>(`/api/account/check-username?u=${encodeURIComponent(newU)}`);
        setCheck(r);
      } catch { setCheck({}); }
    }, 300);
    return () => clearTimeout(id);
  }, [newU, user?.username]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    void wrap(async () => {
      const r = await api<Me>("/api/account/me/username", {
        method: "PATCH",
        body: JSON.stringify({ new_username: newU, password: pw }),
      });
      window.location.reload();
      return `Nom changé pour ${r.username}`;
    });
  };

  const canSubmit = newU !== user?.username && newU.length >= 3 && pw.length > 0
                    && (check.available !== false) && !busy;

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <label className="muted" style={{ fontSize: 12 }}>Nom d'utilisateur</label>
      <input style={input} value={newU} onChange={(e) => setNewU(e.target.value)}
             maxLength={64} minLength={3} />
      {newU !== user?.username && newU.length >= 3 && (
        <div style={{ fontSize: 12, color: check.available ? "#4ade80" : check.available === false ? "#f87171" : "var(--fg-mute)" }}>
          {check.available === true && "✓ disponible"}
          {check.available === false && (check.reason || "✗ ce nom est déjà pris")}
          {check.available === undefined && "…"}
        </div>
      )}
      <label className="muted" style={{ fontSize: 12, marginTop: 6 }}>Mot de passe actuel (confirmation)</label>
      <input style={input} type="password" value={pw} onChange={(e) => setPw(e.target.value)}
             autoComplete="current-password" />
      {msg && <div style={{ color: msg.ok ? "#4ade80" : "#f87171", fontSize: 13 }}>{msg.text}</div>}
      <div><button className="btn primary" type="submit" disabled={!canSubmit}>
        {busy ? "…" : "Enregistrer"}
      </button></div>
    </form>
  );
}

function PasswordTab() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const { msg, busy, wrap } = useSetter();
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (next !== confirm) return;
    void wrap(async () => {
      await api("/api/account/me/password", {
        method: "PATCH",
        body: JSON.stringify({ current_password: cur, new_password: next }),
      });
      setCur(""); setNext(""); setConfirm("");
      return "Mot de passe mis à jour";
    });
  };
  const mismatch = confirm.length > 0 && confirm !== next;
  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <input style={input} type="password" placeholder="Mot de passe actuel"
             value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" />
      <input style={input} type="password" placeholder="Nouveau mot de passe (8 car., lettres + chiffres)"
             value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" minLength={8} />
      <input style={input} type="password" placeholder="Confirmer le nouveau mot de passe"
             value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
      {mismatch && <div style={{ color: "#f87171", fontSize: 13 }}>Les deux mots de passe ne correspondent pas</div>}
      {msg && <div style={{ color: msg.ok ? "#4ade80" : "#f87171", fontSize: 13 }}>{msg.text}</div>}
      <div><button className="btn primary" type="submit" disabled={busy || !cur || !next || mismatch}>
        {busy ? "…" : "Changer le mot de passe"}
      </button></div>
    </form>
  );
}

function EmailTab({ user }: { user: Me | null }) {
  const [email, setEmail] = useState(user?.email ?? "");
  const [pw, setPw] = useState("");
  const { msg, busy, wrap } = useSetter();

  const changeEmail = (e: React.FormEvent) => {
    e.preventDefault();
    void wrap(async () => {
      await api("/api/account/me/email", {
        method: "PATCH",
        body: JSON.stringify({ email, password: pw }),
      });
      // reload so the topbar reflects email_verified=false and the tab shows
      // "en attente de vérification"
      setTimeout(() => window.location.reload(), 900);
      return "Email enregistré. Un mail de vérification vient de partir — regarde ta boîte (et les spams).";
    });
  };

  const sendVerify = () => {
    void wrap(async () => {
      const r = await api<{ delivered: boolean; hint: string | null; already_verified?: boolean }>(
        "/api/account/email/verify/request", { method: "POST", body: "{}" },
      );
      if (r.already_verified) return "Déjà vérifié ✓";
      return r.delivered ? "Mail envoyé — vérifie ta boîte." : (r.hint || "Mail envoyé.");
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="muted" style={{ fontSize: 13 }}>
        Statut : {user?.email_verified ? (
          <span style={{ color: "#4ade80" }}>✓ vérifié</span>
        ) : user?.email ? (
          <span style={{ color: "#fbbf24" }}>en attente de vérification</span>
        ) : (
          <span>pas d'email</span>
        )}
      </div>
      <form onSubmit={changeEmail} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <input style={input} type="email" placeholder="ton@email.com"
               value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input style={input} type="password" placeholder="Mot de passe (confirmation)"
               value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" />
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn primary" type="submit" disabled={busy || !email || !pw}>
            {busy ? "…" : "Mettre à jour l'email"}
          </button>
          {user?.email && !user.email_verified && (
            <button className="btn" type="button" onClick={sendVerify} disabled={busy}>
              Renvoyer le mail de vérification
            </button>
          )}
        </div>
      </form>
      {msg && <div style={{ color: msg.ok ? "#4ade80" : "#f87171", fontSize: 13 }}>{msg.text}</div>}
    </div>
  );
}
